import { isValidElement } from 'react';
import { jsx } from 'react/jsx-runtime';

const IGNORED_KEYS = ["PropTypes"];
const SLOT_NAME = Symbol("slotName");

/**
 * provides a Higher-order-Component that abstracts injection of slottable
 * content in to the component, by providing a streamlined API for managing
 * these slots both in implementation of the component and integration in
 * third-party UI components
 *
 * @example - create a slottable component
 *
 * ```tsx
 * type Props = {
 *   theme: "danger" | "default";
 *   children: ReactNode | ReactNode[];
 * };
 *
 * type Slots = {
 *   Header: {
 *     title: string;
 *     subtitle: string;
 *     children: ReactNode | ReactNode[];
 *   };
 *   Footer: {
 *     children: ReactNode | ReactNode[];
 *   };
 *   ContextMenuButton: {
 *     label: string;
 *     onClick: () => void;
 *   }
 * }
 *
 * const Lightbox = withSlots<Props, Slots>(function Lightbox(props) {
 *   const header = props.slots.assertOne("Header");
 *   const footer = props.slots.findOne("Footer");
 *   const contextMenuButtons = props.slots.findMany("ContextMenuButton");
 *
 *   return (
 *     <dialog>
 *       {contextMenuButtons.length > 0 && <ContextMenu buttons={contextMenuButtons} />}
 *       <div className="header">{header}</div>
 *       {props.children}
 *       {footer && <div className="footer">{footer}</div>}
 *     </dialog>
 *   );
 * }
 * });
 * ```
 *
 * @example - embedding slots in a lightbox
 * ```tsx
 * function MyLightbox() {
 *   return (
 *     <Lightbox>
 *       <Lightbox.ContextMenuButton
 *        label="button 1"
 *        onClick={() => alert("1");}
 *       />
 *       <Lightbox.ContextMenuButton
 *        label="button 2"
 *        onClick={() => alert("2");}
 *       />
 *
 *       <Lightbox.Header
 *         title="Some title"
 *         subtitle="Some subtitle"
 *       >
 *         Some description to be displayed in the header
 *       <Lightbox.Header>
 *
 *       <Lightbox.Footer>
 *         Some footer to be inserted into the button
 *       </Lightbox.Footer>
 *     </Lightbox>
 *   );
 * }
 * ```
 */
function withSlots(Comp) {
  //
  // STEP: create the Slottable higher-order-component which essentially
  // extracts slots and provides correct props to the child component to receive
  // these through the `props.slots` interface
  //
  function Slottable(rawProps) {
    const {
      slots,
      props
    } = makeSlotsApi(rawProps);
    return /*#__PURE__*/jsx(Comp, {
      ...props,
      slots: slots
    });
  }

  //
  // STEP: wrap the HoC in a proxy, so we can intercept and provide integrations
  // for the `<Comp.MySlot>`-interface and intercept them later on above
  //
  return new Proxy(Slottable, {
    get(target, prop) {
      const firstChar = prop.toString().slice(0, 1);
      if (typeof prop !== "string" ||
      // @ts-expect-error React will attempt to access certain keys which are
      // not part of the Slottable API. We need to return these as-is.
      target[prop] ||
      // furthermore we require Slots to start with an uppercased char. If
      // that isn't the case, then it is definitely not a slot which is being
      // accessed.
      firstChar === firstChar.toLowerCase() || IGNORED_KEYS.includes(prop)) {
        // @ts-expect-error See above.
        return target[prop];
      }
      return makeSlot(prop);
    }
  });
}

/**
 * internal helper that translates props.children into our SlotsApi by
 * performing automatic extraction of all <Comp.Slot> children
 */
function makeSlotsApi(rawProps) {
  const {
    children,
    ...props
  } = rawProps;
  const slots = {};
  const childrenWithoutSlots = recursivelyExtractSlotsFromChildren(children, 0, slots);
  return {
    props: {
      children: childrenWithoutSlots,
      ...props
    },
    slots: {
      findAll(slot) {
        return slots[slot] ?? [];
      },
      findOne(slot) {
        return slots[slot]?.[0];
      },
      assertOne(slot) {
        const matchingSlot = slots[slot]?.[0];
        if (matchingSlot === undefined) {
          throw new Error(`withSlots(...).assertOne(${String(slot)}): no matching slot has been inserted`);
        }
        return matchingSlot;
      }
    }
  };
}

/**
 * tiny integration of a recursive React Tree crawler with the one limitation
 * that we only crawl the explicitly given children
 *
 * this means that we do not automatically expand nested children inside e.g.
 * <Lightbox><MyLightboxHeader /></Lightbox>, where <MyLightboxHeader /> would
 * render <Lightbox.Header>
 *
 * for now we have accepted this limitation, as it significantly decreases the
 * complexity and intrusiveness of this step
 */
function recursivelyExtractSlotsFromChildren(children, childIndex, mutableResult) {
  if (!children) {
    return children;
  }
  if (/*#__PURE__*/isValidElement(children)) {
    if (typeof children.type === "function" && SLOT_NAME in children.type) {
      const slotName = String(children.type[SLOT_NAME]);

      // if this element is actually a slot, then extract it's props and
      // remove it from the rendered output
      mutableResult[slotName] ??= [];
      mutableResult[slotName]?.push(children.props);
      return null;
    }
    if ("children" in children.props) {
      // ... otherwise if we encountered a "normal" React element, then we need
      // to recursively parse it's nested children if relevant
      return {
        ...children,
        key: children.key ?? childIndex,
        props: {
          ...children.props,
          children: recursivelyExtractSlotsFromChildren(children.props["children"], 0, mutableResult)
        }
      };
    }

    // if we get here, there is no nested content - so return the element "as
    // is"
    return {
      ...children,
      key: children.key ?? childIndex
    };
  }
  if (Array.isArray(children)) {
    return children.map((child, childIndex) => recursivelyExtractSlotsFromChildren(child, childIndex, mutableResult));
  }

  // if we get here, we have no idea what content was received - leave it as is
  return children;
}

/**
 * make a Slot component with the slotName provided under or internal symbol, so
 * it can be dynamically infered when parsing children at runtime
 */
function makeSlot(slotName) {
  return Object.assign(function Slot() {
    return null;
  }, {
    [SLOT_NAME]: slotName
  });
}

export { withSlots };
//# sourceMappingURL=hoc.with-slots.js.map
